import { MAX_PERIOD_DAYS } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';
import {
    MATCH_POLICIES,
    MATCH_POLICY,
    MAX_TERM_LENGTH,
    compilePattern,
    definitionErrors,
} from './brand';

/**
 * The scope this report runs under: the shared one, plus the brand definition every request is
 * filtered by.
 *
 * The definition is part of the scope rather than a view over fetched rows, because it is in the
 * request: the brand and non-brand sides are two differently filtered asks of Search Console, so
 * changing a term changes what was fetched and not just what is drawn. That is why it sits behind
 * the same Apply as the dates, and why the applied pattern travels with the figures into the
 * coverage block and the export.
 *
 * The window is the shared headline bound. Only the evidence block carries a query dimension, and
 * it shortens its own window through the shared detail helper and says so, rather than holding
 * the whole report to the shorter bound for the sake of one block.
 */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        policy: MATCH_POLICY.LITERAL,
        // Session-only, in the order they were added. Nothing is stored in the account or in the
        // browser; the definition travels with the applied scope in the page's address, which is
        // the whole of its lifetime.
        terms: [],
        pattern: '',
    };
}

export function resolveReportScope(draft, t) {
    const scope = resolveScope(draft, t, { maxDays: MAX_PERIOD_DAYS });

    const policy = MATCH_POLICIES.includes(draft.policy) ? draft.policy : MATCH_POLICY.LITERAL;
    const terms = (draft.terms ?? []).filter((term) => term?.match?.trim());
    const pattern = draft.pattern ?? '';

    const definition = { policy, terms, pattern };
    scope.errors.push(...definitionErrors(definition, t));

    return {
        ...scope,
        policy,
        terms,
        pattern,
        // The one expression both sides are read through, or null when the definition is not
        // usable — in which case the scope already carries the error that says why.
        compiledPattern: compilePattern(definition),
    };
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['policy', 'terms', 'pattern'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own controls too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

/**
 * One term as the address can carry it, or null.
 *
 * A query string is typed by hand as easily as it is copied, so a term is taken only when it is
 * text with something in it and is no longer than a typed one may be. The name is a label the
 * user gave it and is never sent to Search Console, so it is taken as-is but bounded too.
 */
function readTerm(term) {
    if (!term || typeof term !== 'object') return null;
    if (typeof term.match !== 'string' || !term.match.trim()) return null;
    if (term.match.length > MAX_TERM_LENGTH) return null;

    return {
        name: typeof term.name === 'string' ? term.name.slice(0, MAX_TERM_LENGTH) : '',
        match: term.match,
    };
}

/** The draft this report starts from when its address already describes one. */
export function reportDraftFromParams(params) {
    const draft = scopeDraftFromParams(params);

    if (MATCH_POLICIES.includes(params.policy)) draft.policy = params.policy;
    if (params.pattern) draft.pattern = params.pattern;

    if (params.terms) {
        try {
            const parsed = JSON.parse(params.terms);
            const terms = (Array.isArray(parsed) ? parsed : []).map(readTerm).filter(Boolean);
            if (terms.length) draft.terms = terms;
        } catch (err) {
            // A terms parameter that is not JSON is not a report anyone meant to open; the page
            // starts with no definition rather than refusing to open at all.
            console.log(err);
        }
    }

    return draft;
}

/** The applied scope as query parameters. */
export function reportParamsFromScope(scope) {
    return {
        ...scopeParamsFromScope(scope),
        policy: scope.policy,
        terms: scope.terms?.length ? JSON.stringify(scope.terms) : undefined,
        pattern: scope.policy === MATCH_POLICY.REGEX ? scope.pattern || undefined : undefined,
    };
}
